import React, { useState, useEffect } from 'react';
import GenericModal from '@ui-components/Modal/GenericModal';
import GenericButton from '@ui-components/Button/GenericButton';
import GenericInput from '@ui-components/Input/GenericInput';
import GenericSwitch from '@ui-components/Switch/GenericSwitch';
import type { FortiFlexAlertRecipient } from '../hooks/useFortiFlexData';

interface AlertRecipientFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: { email: string; name: string; enabled: boolean }) => void;
    recipient?: FortiFlexAlertRecipient | null;
    loading?: boolean;
}

const AlertRecipientFormModal: React.FC<AlertRecipientFormModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    recipient,
    loading,
}) => {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [enabled, setEnabled] = useState(true);
    const isEdit = Boolean(recipient);

    useEffect(() => {
        if (recipient) {
            setEmail(recipient.email);
            setName(recipient.name || '');
            setEnabled(recipient.enabled);
        } else {
            setEmail('');
            setName('');
            setEnabled(true);
        }
    }, [recipient, isOpen]);

    return (
        <GenericModal
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? 'Edit Alert Recipient' : 'Add Alert Recipient'}
            size="sm"
            footer={
                <>
                    <GenericButton
                        variant="secondary"
                        size="md"
                        label="Cancel"
                        onClick={onClose}
                    />
                    <GenericButton
                        variant="brand"
                        size="md"
                        label={loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                        onClick={() => onSubmit({ email, name, enabled })}
                        disabled={loading || !email}
                        loading={loading}
                    />
                </>
            }
        >
            <div className="space-y-4">
                <GenericInput
                    label="Email"
                    type="email"
                    value={email}
                    onChange={setEmail}
                    placeholder="user@example.com"
                />
                <GenericInput
                    label="Name"
                    value={name}
                    onChange={setName}
                    placeholder="Display name (optional)"
                />
                <div className="flex items-center justify-between">
                    <div>
                        <span className="text-sm font-medium text-semantic-text-primary">
                            Enabled
                        </span>
                        <p className="text-xs text-semantic-text-secondary mt-0.5">
                            Disabled recipients will not receive alert emails
                        </p>
                    </div>
                    <GenericSwitch
                        checked={enabled}
                        onChange={setEnabled}
                    />
                </div>
            </div>
        </GenericModal>
    );
};

export default AlertRecipientFormModal;
